export type ScreenMethods = {
  // Screen layout functions
  getScreen: (id: number) => Promise<OnlyScreenData>;
  getScreens: () => Promise<OnlyScreenData[]>;
  getOverlays: (id: number) => Promise<Overlays>;
  setScreens: (
    screens: { id: number; isShowing?: boolean; screenType?: ScreenType }[],
  ) => void;
  getGridData: (id: number) => Promise<GridResult>;
  setFullScreen: (id: number, gridPosition: GridPosition) => void;
  setHalfScreen: (id: number, gridPosition: GridPosition) => void;
  setHalfScreenLeft: (id: number, gridPosition: GridPosition) => void;
  setHalfScreenRight: (id: number, gridPosition: GridPosition) => void;
  clearPosition: (id: number, position: GridPosition) => void;
  setGridLayout: (id: number, gridLayout: GridLayout) => void;
  setNCams: (id: number, nCams: NCams) => void;
  setVideoAspectRatio: (
    id: number,
    videoAspectRatio: VideoAspectRatioEnum,
  ) => void;
  getPositionData: (id: number, position: GridPosition) => Promise<CellData>;
  setPositionData: (
    id: number,
    position: GridPosition,
    positionData: PositionData | null,
  ) => void;
  swapCamerasInPosition: (
    id: number,
    position: GridPosition,
    sourceCam: number,
    targetCam: number,
  ) => void;
  cycleCamerasInPosition: (id: number, position: GridPosition) => void;
  cycleToNextPen: (id: number) => {
    success: boolean;
    newPosition?: GridPosition;
  };
  cycleToPreviousPen: (id: number) => {
    success: boolean;
    newPosition?: GridPosition;
  };
  loadMachines: (
    machinesToLoad: MachinesToLoad,
    options?: LoadMachinesOptions,
  ) => Promise<ScreenLayout[]>;
  resetScreenConfiguration: () => Promise<void>;
  saveScreenConfiguration: () => Promise<void>;
  onPositionDataChanged: (cb: () => void) => () => void;
  onNCamsChanged: (cb: () => void) => () => void;
  onIntegrationChanged: (cb: () => void) => () => void;
  onSelectedCameraChanged: (cb: () => void) => () => void;
  getPinData: (id: number, position: GridPosition) => Promise<PinData | null>;
  setPinData: (
    id: number,
    position: GridPosition,
    pinData: PinData | null,
  ) => void;
  getAllPins: () => Promise<
    Array<{ screenId: number; gridId: GridId; pin: PinData }>
  >;
  getCamerasOnPinnedCells: () => Promise<number[]>;
  onPinDataChanged: (cb: () => void) => () => void;
  updateScreenType: (id: number, screenType: string) => void;
};

export type ScreenType = "#/video" | "#/feeding" | "welcome";

export type NCams = 1 | 2 | 3 | 4;
export const GRID_ROWS = [1, 2, 3, 4] as const;
export const GRID_COLUMNS = [1, 2, 3, 4, 5, 6] as const;
export type RowNumber = (typeof GRID_ROWS)[number];
export type ColumnNumber = (typeof GRID_COLUMNS)[number];
export enum VideoAspectRatioEnum {
  Original = 1,
  Stretch = 2,
  Crop = 3,
}

export type GridLayout = {
  rows: RowNumber;
  columns: ColumnNumber;
};

export type PinType = "feeding" | "alerts";

export type FeedingPinData = {
  type: "feeding";
  feedlineId: string;
  siteId: number;
  machineId: number;
};

export type AlertsFeedlinePinData = {
  type: "alerts";
  scope: "feedline";
  feedlineId: string;
  siteId: number;
  machineId: number;
};

export type AlertsCagePinData = {
  type: "alerts";
  scope: "cage";
  cageId: number;
  siteId: number;
  machineId: number;
};

export type AlertsPinData = AlertsFeedlinePinData | AlertsCagePinData;

export type PinData = FeedingPinData | AlertsPinData;

export type CellData<Cams extends number[] = number[]> = {
  machineId: number;
  cageId: number;
  cameras: Cams;
  pin?: PinData;
} | null;

export type GridId = `${RowNumber}-${ColumnNumber}`;

export const ALL_GRID_IDS: GridId[] = GRID_ROWS.flatMap((row) =>
  GRID_COLUMNS.map((column) => `${row}-${column}` as GridId),
);

export const isGridLayout = (gridLayout: GridLayout): boolean =>
  (GRID_ROWS as readonly number[]).includes(gridLayout.rows) &&
  (GRID_COLUMNS as readonly number[]).includes(gridLayout.columns);

export const isGridId = (gridId: string): gridId is GridId =>
  (ALL_GRID_IDS as string[]).includes(gridId);

export const isGridIdInside = (gridId: GridId, gridLayout: GridLayout) => {
  const [row, column] = gridId.split("-").map(Number);
  return row <= gridLayout.rows && column <= gridLayout.columns;
};

export type GridMap<Cams extends number[] = number[]> = Record<
  GridId,
  CellData<Cams>
>;

export type FeedPageSites = {
  siteIds: number[];
};

export type FeedScreenData = {
  sites: FeedPageSites;
  columns: string[];
  copilotColumns: string[];
  filters: string[];
};

export type VideoScreenData = {
  // Fullscreen - top layer
  fullScreen: GridId | null;

  // Focused - middle layer
  halfScreenLeft: GridId | null;
  halfScreenRight: GridId | null;

  // Grids - bottom layer
  gridLayout: GridLayout;
  cameraGrid: GridMap<[number]>;
  cageGrid: GridMap<[number, ...number[]]>;
  nCams: NCams;
  videoAspectRatio: VideoAspectRatioEnum;
};

export type ScreenLayout = {
  id: number;
  isShowing: boolean;
  screenType: ScreenType;
  screenData: VideoScreenData;
  feedScreenData: FeedScreenData;
};

// Types for methods
export type OnlyScreenData = Pick<
  ScreenLayout,
  "id" | "isShowing" | "screenType"
>;
type Overlays = Pick<
  ScreenLayout["screenData"],
  "fullScreen" | "halfScreenLeft" | "halfScreenRight"
>;
export type GridResult = {
  gridLayout: GridLayout;
  gridData: GridMap<[number, ...number[]]>;
  nCams: NCams;
  videoAspectRatio: VideoAspectRatioEnum;
};

export type MachinesToLoad = Record<
  number,
  Array<{ cageId: number; cameras: number[] }>
>;

export type LoadMachinesOptions = {
  knownMachineIds?: number[];
};

export type GridPosition = {
  row: RowNumber;
  column: ColumnNumber;
};

export type CagePositionData = {
  machineId: number;
  cageId: number;
  cameras?: number[];
};

export type CameraPositionData = {
  machineId: number;
  cageId: number;
  cameraId: number;
};

export type PositionData = CagePositionData | CameraPositionData;
